import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  motion,
  AnimatePresence,
  useReducedMotion,
  useDragControls,
} from "framer-motion";
import {
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaWhatsapp,
  FaMap,
  FaStreetView,
  FaSatellite,
  FaCheckCircle,
} from "react-icons/fa";

/* ===================== Design Tokens (Light/Dark) ===================== */
function useTokens() {
  const [isDark, setIsDark] = useState(false);
  useEffect(() => {
    const root = document.documentElement;
    const byClass = root.classList.contains("dark");
    const byMedia =
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches;
    setIsDark(byClass || byMedia);
  }, []);
  return useMemo(
    () =>
      isDark
        ? {
            text: "#FFFFFF",
            muted: "rgba(255,255,255,0.78)",
            ring: "rgba(255,255,255,0.12)",
            wash: "rgba(255,255,255,0.05)",
            panel: "rgba(16,21,34,0.75)",
            shadow: "0 28px 64px rgba(0,0,0,0.45)",
            bgStart: "#0B1220",
            bgMid: "#0B1325",
            bgEnd: "#000",
            grid: "rgba(255,255,255,0.08)",
          }
        : {
            text: "#0B1220",
            muted: "rgba(17,24,39,0.75)",
            ring: "rgba(2,6,23,0.10)",
            wash: "rgba(2,6,23,0.04)",
            panel: "rgba(255,255,255,0.92)",
            shadow: "0 26px 64px rgba(2,6,23,0.12)",
            bgStart: "#F6FAFF",
            bgMid: "#EEF3F9",
            bgEnd: "#FFFFFF",
            grid: "rgba(0,0,0,0.08)",
          },
    [isDark]
  );
}

/* ===================== Floating Orb ===================== */
const FloatingOrb = ({ style }) => (
  <motion.div
    className="absolute w-6 h-6 bg-gradient-to-br from-emerald-500 to-cyan-400 rounded-full opacity-20 blur-sm"
    animate={{
      x: [0, 40, -40, 0],
      y: [0, -22, 22, 0],
      scale: [1, 1.25, 0.85, 1],
    }}
    transition={{
      duration: 6,
      repeat: Infinity,
      ease: "easeInOut",
      delay: Math.random(),
    }}
    style={style}
  />
);

/* ===================== Toast ===================== */
function Toast({ message, type = "success" }) {
  return (
    <motion.div
      initial={{ y: 24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 24, opacity: 0 }}
      className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 rounded-xl px-4 py-3 backdrop-blur ring-1 ${
        type === "success" ? "text-emerald-900" : "text-red-900"
      }`}
      style={{
        background:
          type === "success"
            ? "linear-gradient(135deg, rgba(16,185,129,0.18), rgba(6,182,212,0.18))"
            : "linear-gradient(135deg, rgba(239,68,68,0.18), rgba(251,113,133,0.18))",
        borderColor: "rgba(255,255,255,0.25)",
      }}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-2">
        <FaCheckCircle className="opacity-80" />
        <span className="font-medium">{message}</span>
      </div>
    </motion.div>
  );
}

/* ===================== Contact Info ===================== */
function ContactInfo({ isMobile, tokens }) {
  const controls = useDragControls();
  const constraintsRef = useRef(null);
  const prefersReduced = useReducedMotion();

  const items = [
    { icon: FaEnvelope, text: "monkeyytech05@gmail.com", href: "mailto:monkeyytech05@gmail.com", label: "Email" },
    { icon: FaPhone, text: "+91 93160 36603", href: "https://wa.me/919316036603", label: "Phone 1", copy: "9316036603" },
    { icon: FaPhone, text: "+91 70162 89457", href: "https://wa.me/917016289457", label: "Phone 2", copy: "7016289457" },
    { icon: FaMapMarkerAlt, text: "Surat, India", label: "Location" },
  ];

  const socials = [
    { icon: FaFacebook, href: "https://facebook.com/", color: "#3B5998", label: "Facebook" },
    { icon: FaTwitter, href: "https://x.com/MonkeyyTech/", color: "#1DA1F2", label: "Twitter / X" },
    { icon: FaInstagram, href: "https://www.instagram.com/monkeyy.tech?igsh=MTFsZzRuZjd4cTZkMQ==", color: "#E1306C", label: "Instagram" },
    { icon: FaWhatsapp, href: "https://wa.me/919316036603", color: "#25D366", label: "WhatsApp" },
  ];

  return (
    <div className="relative" ref={constraintsRef}>
      <motion.div
        drag={!isMobile && !prefersReduced}
        dragControls={controls}
        dragConstraints={constraintsRef}
        dragElastic={0.18}
        whileHover={{ y: -2 }}
        className="relative rounded-2xl p-6 lg:p-8 backdrop-blur ring-1"
        style={{
          background: tokens.panel,
          borderColor: tokens.ring,
          boxShadow: tokens.shadow,
        }}
      >
        <h2 className="text-2xl sm:text-3xl font-semibold mb-6">Contact</h2>

        <ul className="space-y-4">
          {items.map((it) => (
            <li key={it.text}>
              <a
                href={it.href || "#"}
                className="group relative flex items-center"
                onClick={async (e) => {
                  if (!it.copy) return;
                  e.preventDefault();
                  try { await navigator.clipboard.writeText(it.copy); } catch {}
                }}
                aria-label={it.label}
                style={{ color: tokens.text }}
              >
                <it.icon className="mr-3 text-lg opacity-90" />
                <span className="font-medium">{it.text}</span>
                <span className="ml-2 text-xs opacity-70 group-hover:opacity-100">
                  {it.copy ? "(tap to copy)" : ""}
                </span>
              </a>
            </li>
          ))}
        </ul>

        <div className="mt-8">
          <h3 className="text-lg font-semibold mb-3">Follow us</h3>
          <div className="flex gap-3">
            {socials.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                className="group relative inline-flex h-10 w-10 items-center justify-center rounded-full ring-1 transition"
                style={{ borderColor: tokens.ring, background: tokens.wash }}
              >
                <s.icon className="text-base" />
                <span
                  className="pointer-events-none absolute inset-0 rounded-full opacity-0 group-hover:opacity-20 transition"
                  style={{ background: s.color }}
                />
              </a>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

/* ===================== Contact Form ===================== */
function ContactForm({ tokens, setToast }) {
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    topic: "General",
    message: "",
    hp: "",
  });
  const [errors, setErrors] = useState({});

  const fields = [
    { id: "name", type: "text", label: "Name", placeholder: "Your name" },
    { id: "mobile", type: "tel", label: "Mobile", placeholder: "10-digit number", pattern: "^[0-9]{10}$" },
    { id: "email", type: "email", label: "Email (optional)", placeholder: "you@domain.com" },
  ];

  const validate = () => {
    const e = {};
    if (!formData.name.trim()) e.name = "Name is required";
    if (!/^\d{10}$/.test(formData.mobile)) e.mobile = "Enter 10-digit number";
    if (!formData.message.trim()) e.message = "Message is required";
    return e;
  };

  const sendViaWhatsApp = () => {
    const msg = `Name: ${formData.name}
Mobile: ${formData.mobile}
Email: ${formData.email || "-"}
Topic: ${formData.topic}
Message: ${formData.message}
Instagram: https://www.instagram.com/monkeyy.tech?igsh=MTFsZzRuZjd4cTZkMQ==`;
    const url = `https://wa.me/919316036603?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (formData.hp) return; // bot honeypot
    const eMap = validate();
    setErrors(eMap);
    if (Object.keys(eMap).length) return;

    try {
      sendViaWhatsApp();
      setToast({ type: "success", msg: "WhatsApp opened — send your message!" });
      setFormData({ name: "", mobile: "", email: "", topic: "General", message: "", hp: "" });
    } catch {
      setToast({ type: "error", msg: "Could not open WhatsApp. Try again." });
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      className="relative rounded-2xl p-6 lg:p-8 backdrop-blur ring-1"
      style={{ background: tokens.panel, borderColor: tokens.ring, boxShadow: tokens.shadow }}
    >
      <h2 className="text-2xl sm:text-3xl font-semibold mb-6">Send a message</h2>

      <input
        type="text" name="company" autoComplete="off" value={formData.hp}
        onChange={(e) => setFormData((s) => ({ ...s, hp: e.target.value }))}
        className="hidden" tabIndex={-1}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {fields.map((f) => (
          <div key={f.id} className="flex flex-col">
            <label htmlFor={f.id} className="text-sm font-medium mb-1">{f.label}</label>
            <input
              id={f.id}
              type={f.type}
              inputMode={f.type === "tel" ? "numeric" : undefined}
              pattern={f.pattern}
              placeholder={f.placeholder}
              value={formData[f.id]}
              onChange={(e) => setFormData((s) => ({ ...s, [f.id]: e.target.value }))}
              className={`w-full rounded-lg px-4 py-2 outline-none ring-1 transition ${errors[f.id] ? "ring-red-400" : ""}`}
              style={{ background: tokens.wash, borderColor: tokens.ring, color: tokens.text }}
              aria-invalid={!!errors[f.id]}
              aria-describedby={errors[f.id] ? `${f.id}-err` : undefined}
            />
            {errors[f.id] && <span id={`${f.id}-err`} className="text-red-500 text-xs mt-1">{errors[f.id]}</span>}
          </div>
        ))}

        <div className="flex flex-col">
          <label htmlFor="topic" className="text-sm font-medium mb-1">Topic</label>
          <select
            id="topic"
            value={formData.topic}
            onChange={(e) => setFormData((s) => ({ ...s, topic: e.target.value }))}
            className="w-full rounded-lg px-4 py-2 outline-none ring-1"
            style={{ background: tokens.wash, borderColor: tokens.ring, color: tokens.text }}
          >
            <option>General</option>
            <option>Project Inquiry</option>
            <option>Support</option>
            <option>Partnership</option>
          </select>
        </div>
        <div className="hidden sm:block" />
      </div>

      <div className="mt-4">
        <label htmlFor="message" className="text-sm font-medium mb-1 block">Message</label>
        <textarea
          id="message" rows={5} placeholder="Tell us about your idea or issue…"
          value={formData.message}
          onChange={(e) => setFormData((s) => ({ ...s, message: e.target.value }))}
          className={`w-full rounded-lg px-4 py-2 outline-none ring-1 transition ${errors.message ? "ring-red-400" : ""}`}
          style={{ background: tokens.wash, borderColor: tokens.ring, color: tokens.text }}
          aria-invalid={!!errors.message}
          aria-describedby={errors.message ? "message-err" : undefined}
        />
        {errors.message && <span id="message-err" className="text-red-500 text-xs mt-1 block">{errors.message}</span>}
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <motion.button
          whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} type="submit"
          className="inline-flex justify-center items-center px-6 py-3 rounded-xl font-semibold text-black shadow-lg"
          style={{ background: "linear-gradient(135deg, var(--brand, #10B981), var(--accent,#06B6D4))" }}
        >
          Send via WhatsApp
        </motion.button>
        <a
          href="mailto:monkeyytech05@gmail.com"
          className="inline-flex justify-center items-center px-6 py-3 rounded-xl font-semibold ring-1"
          style={{ borderColor: tokens.ring, background: tokens.wash, color: tokens.text }}
        >
          Prefer Email
        </a>
      </div>
    </form>
  );
}

/* ===================== Map Section ===================== */
function MapSection({ isMobile, tokens }) {
  const [view, setView] = useState("map");
  const [loaded, setLoaded] = useState(false);

  const mapUrls = {
    map: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3679.976792770693!2d72.79769187544048!3d21.17024088068817!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be04e5940973e5b%3A0x8b6c6f3c7a8f4e7e!2sSurat%2C%20Gujarat%2C%20India!5e0!3m2!1sen!2sus!4v1627500000000!5m2!1sen!2sus",
    street:
      "https://www.google.com/maps/embed?pb=!4v1627500000000!6m8!1m7!1sCAoSLEFGMVFpcE5JdXlpeGd5d0RkUnlqUEZ4b0RFRWZsRzVJOUlSeW1GTkxrQ1pU!2m2!1d21.17024!2d72.83106!3f0!4f0!5f0.7820865974627469",
    satellite:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3679.976792770693!2d72.79769187544048!3d21.17024088068817!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be04e5940973e5b%3A0x8b6c6f3c7a8f4e7e!2sSurat%2C%20Gujarat%2C%20India!5e1!3m2!1sen!2sus!4v1627500000000!5m2!1sen!2sus",
  };

  useEffect(() => {
    if (loaded) return;
    const t = setTimeout(() => setLoaded(true), 800);
    return () => clearTimeout(t);
  }, [loaded]);

  // swipe switcher on mobile (fixed)
  const onTouchStart = (e) => {
    if (!isMobile) return;
    const views = ["map", "street", "satellite"];
    const startX = e.touches[0].clientX;
    e.currentTarget.ontouchend = (e2) => {
      const dx = e2.changedTouches[0].clientX - startX;
      if (Math.abs(dx) < 50) return;
      const i = views.indexOf(view);
      const next = dx < 0 ? Math.min(i + 1, views.length - 1) : Math.max(i - 1, 0);
      setView(views[next]);
    };
  };

  const tabs = [
    { key: "map", label: "Map View", icon: FaMap },
    { key: "street", label: "Street", icon: FaStreetView },
    { key: "satellite", label: "Satellite", icon: FaSatellite },
  ];

  return (
    <motion.section
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      className="mt-12"
      onTouchStart={onTouchStart}
      aria-labelledby="map-heading"
    >
      <h2 id="map-heading" className="text-center text-2xl sm:text-3xl font-semibold mb-6">Our location</h2>

      <div className="rounded-2xl p-6 backdrop-blur ring-1"
           style={{ background: tokens.panel, borderColor: tokens.ring, boxShadow: tokens.shadow }}>
        <div className="flex flex-wrap gap-2 mb-4">
          {tabs.map((t) => {
            const active = view === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setView(t.key)}
                className="flex items-center px-4 py-2 rounded-lg font-medium transition"
                style={{
                  background: active
                    ? "linear-gradient(135deg, var(--brand,#10B981), var(--accent,#06B6D4))"
                    : tokens.wash,
                  color: active ? "#000" : tokens.text,
                  border: `1px solid ${tokens.ring}`,
                }}
                aria-pressed={active}
              >
                <t.icon className="mr-2" size={18} />
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="aspect-video rounded-xl overflow-hidden ring-1" style={{ borderColor: tokens.ring }}>
          {loaded ? (
            <iframe
              title={`MonkeyTech Location — ${view}`}
              src={mapUrls[view]}
              width="100%"
              height="100%"
              frameBorder="0"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              className="block"
            />
          ) : (
            <div className="w-full h-full grid place-items-center">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="h-8 w-8 rounded-full border-2 border-emerald-500 border-t-transparent"
              />
            </div>
          )}
        </div>
      </div>
    </motion.section>
  );
}

/* ===================== Main Page ===================== */
export default function Contact() {
  const [isMobile, setIsMobile] = useState(false);
  const tokens = useTokens();
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <main
      className="relative min-h-screen font-inter overflow-hidden"
      role="main"
      aria-label="MonkeyTech Contact Page"
      style={{ color: tokens.text }}
    >
      {/* Background: gradient + orbs + grid */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0"
             style={{ background: `linear-gradient(135deg, ${tokens.bgStart}, ${tokens.bgMid}, ${tokens.bgEnd})` }} />
        <div className="absolute -top-24 -left-24 h-[34rem] w-[34rem] rounded-full blur-3xl"
             style={{ background: "radial-gradient(closest-side, rgba(16,185,129,0.22), transparent)" }} />
        <div className="absolute bottom-[-8rem] right-[-8rem] h-[30rem] w-[30rem] rounded-full blur-3xl"
             style={{ background: "radial-gradient(closest-side, rgba(6,182,212,0.18), transparent)" }} />
        <div className="absolute inset-0 opacity-[0.06] [mask-image:linear-gradient(to_bottom,black,transparent_95%)]"
             style={{
               backgroundImage: `linear-gradient(${tokens.grid} 1px, transparent 1px), linear-gradient(90deg, ${tokens.grid} 1px, transparent 1px)`,
               backgroundSize: "44px 44px",
             }} />
      </div>

      {Array.from({ length: 6 }).map((_, i) => (
        <FloatingOrb key={i} style={{ top: `${18 + i * 13}%`, left: `${8 + i * 16}%` }} />
      ))}

      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="text-center py-16 relative z-10"
      >
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">
          Get in <span className="bg-clip-text text-transparent bg-gradient-to-r from-emerald-500 to-cyan-400">touch</span>
        </h1>
        <p className="mt-4 text-lg sm:text-xl max-w-3xl mx-auto" style={{ color: tokens.muted }}>
          We’d love to hear from you — partnerships, projects, or support.
        </p>
      </motion.header>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="lg:grid lg:grid-cols-2 lg:gap-10 space-y-8 lg:space-y-0">
          <ContactInfo isMobile={isMobile} tokens={tokens} />
          <ContactForm tokens={tokens} setToast={setToast} />
        </div>

        <MapSection isMobile={isMobile} tokens={tokens} />

        {/* Bottom CTA strip */}
        <div className="mt-10 grid place-items-center">
          <a
            href="mailto:monkeyytech05@gmail.com"
            className="inline-flex items-center gap-2 rounded-full px-5 py-3 font-semibold ring-1"
            style={{ borderColor: tokens.ring, background: tokens.wash, color: tokens.text }}
          >
            <FaEnvelope /> Prefer email? monkeyytech05@gmail.com
          </a>
        </div>
      </div>

      {/* Toasts */}
      <AnimatePresence>
        {toast && <Toast message={toast.msg} type={toast.type} />}
      </AnimatePresence>

      {/* Helpers */}
      <style>{`
        .font-inter{font-family:Inter,ui-sans-serif,system-ui,-apple-system,'Segoe UI',Roboto,Helvetica,Arial}
      `}</style>

      {/* JSON-LD ContactPoint */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "MonkeyTech",
            url: "https://www.monkeytech.com",
            contactPoint: [{
              "@type": "ContactPoint",
              telephone: "+91-93160-36603",
              contactType: "customer support",
              areaServed: "IN",
              availableLanguage: ["en", "hi"],
            }],
            address: {
              "@type": "PostalAddress",
              addressLocality: "Surat",
              addressRegion: "Gujarat",
              addressCountry: "IN",
            },
          }),
        }}
      />
    </main>
  );
}
